import { readWaterJSON, writeWaterJSON } from './waterStorage'
import { createId } from './id'

import type { InventoryCategory, InventoryItem, InventoryUnit } from '../types/inventory'
export type { InventoryCategory, InventoryItem, InventoryUnit } from '../types/inventory'

type InventoryPayloadV1 = {
  version: 1
  items: InventoryItem[]
}

const STORAGE_KEY = 'warehouse:v1'

const nowIso = (): string => new Date().toISOString()

const isPlainObject = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
)

const toOptionalIsoDate = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return undefined
  return trimmed
}

const normalizeMetadata = (candidate: Record<string, unknown>, createdAt: string): Record<string, unknown> | undefined => {
  const rawMeta = candidate.metadata
  const baseMeta = isPlainObject(rawMeta) ? rawMeta : {}
  const createdFallback = createdAt.length >= 10 ? createdAt.slice(0, 10) : undefined
  const receivedOn = toOptionalIsoDate((baseMeta as any).receivedOn) ?? createdFallback
  const expiresOn = toOptionalIsoDate((baseMeta as any).expiresOn)

  if (!receivedOn && !expiresOn && Object.keys(baseMeta).length === 0) return undefined

  return {
    ...baseMeta,
    ...(receivedOn ? { receivedOn } : null),
    ...(expiresOn ? { expiresOn } : null)
  }
}

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (typeof value === 'string') {
    const trimmed = value.trim().replace(',', '.')
    if (!trimmed) return null
    const parsed = Number(trimmed)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const normalizeCategory = (value: unknown): InventoryCategory => {
  if (typeof value !== 'string') return 'other'
  switch (value.trim().toLowerCase()) {
    case 'feed':
      return 'feed'
    case 'chemical':
      return 'chemical'
    case 'equipment':
      return 'equipment'
    default:
      return 'other'
  }
}

const normalizeUnit = (value: unknown): InventoryUnit => {
  if (typeof value !== 'string') return 'pcs'
  switch (value.trim().toLowerCase()) {
    case 'kg':
      return 'kg'
    case 'l':
    case 'lt':
    case 'liter':
    case 'litre':
      return 'l'
    default:
      return 'pcs'
  }
}

const normalizeItem = (candidate: unknown): InventoryItem | null => {
  if (!isPlainObject(candidate)) return null

  const id = typeof candidate.id === 'string' ? candidate.id.trim() : ''
  const farmId = typeof candidate.farmId === 'string' ? candidate.farmId.trim() : ''
  const name = typeof candidate.name === 'string' ? candidate.name.trim() : ''

  if (!id || !farmId || !name) return null

  const onHand = toNumber(candidate.onHand)
  if (onHand === null || onHand < 0) return null

  const reorderPoint = toNumber(candidate.reorderPoint)

  const createdAt = typeof candidate.createdAt === 'string' ? candidate.createdAt : nowIso()
  const updatedAt = typeof candidate.updatedAt === 'string' ? candidate.updatedAt : createdAt

  const metadata = normalizeMetadata(candidate, createdAt)

  return {
    id,
    farmId,
    name,
    category: normalizeCategory(candidate.category),
    unit: normalizeUnit(candidate.unit),
    onHand,
    reorderPoint: reorderPoint !== null && reorderPoint >= 0 ? reorderPoint : undefined,
    usageFeedType: typeof candidate.usageFeedType === 'string' ? candidate.usageFeedType.trim() : undefined,
    notes: typeof candidate.notes === 'string' ? candidate.notes.trim() : undefined,
    metadata,
    createdAt,
    updatedAt
  }
}

export const loadInventoryItems = (): InventoryItem[] => {
  const raw = readWaterJSON<unknown>(STORAGE_KEY, { version: 1, items: [] })

  if (isPlainObject(raw) && raw.version === 1 && Array.isArray(raw.items)) {
    const normalized = raw.items
      .map(normalizeItem)
      .filter((item): item is InventoryItem => Boolean(item))
    return normalized.sort((a, b) => a.name.localeCompare(b.name))
  }

  if (Array.isArray(raw)) {
    const normalized = raw
      .map(normalizeItem)
      .filter((item): item is InventoryItem => Boolean(item))
    saveInventoryItems(normalized)
    return normalized.sort((a, b) => a.name.localeCompare(b.name))
  }

  return []
}

export const saveInventoryItems = (items: InventoryItem[]): void => {
  const payload: InventoryPayloadV1 = { version: 1, items }
  writeWaterJSON(STORAGE_KEY, payload)
}

export const createInventoryItem = (args: {
  farmId: string
  name: string
  category: InventoryCategory
  unit: InventoryUnit
  onHand: number
  reorderPoint?: number
  usageFeedType?: string
  notes?: string
  metadata?: Record<string, unknown>
}): InventoryItem => {
  const timestamp = nowIso()
  return {
    id: createId('inv'),
    farmId: args.farmId,
    name: args.name.trim(),
    category: args.category,
    unit: args.unit,
    onHand: args.onHand,
    reorderPoint: args.reorderPoint,
    usageFeedType: args.usageFeedType?.trim() || undefined,
    notes: args.notes?.trim() || undefined,
    metadata: args.metadata,
    createdAt: timestamp,
    updatedAt: timestamp
  }
}

export const upsertInventoryItem = (item: InventoryItem): InventoryItem[] => {
  const existing = loadInventoryItems()
  const timestamp = nowIso()

  const nextItem: InventoryItem = {
    ...item,
    name: item.name.trim(),
    usageFeedType: item.usageFeedType?.trim() || undefined,
    notes: item.notes?.trim() || undefined,
    updatedAt: timestamp
  }

  const idx = existing.findIndex((entry) => entry.id === item.id)
  const next = idx >= 0
    ? existing.map((entry, index) => (index === idx ? nextItem : entry))
    : existing.concat(nextItem)

  const sorted = next.slice().sort((a, b) => a.name.localeCompare(b.name))
  saveInventoryItems(sorted)
  return sorted
}

export const deleteInventoryItem = (id: string): InventoryItem[] => {
  const trimmed = id.trim()
  if (!trimmed) return loadInventoryItems()
  const next = loadInventoryItems().filter((item) => item.id !== trimmed)
  saveInventoryItems(next)
  return next
}
